import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import {
	faChevronLeft,
	faChevronRight,
	faRightToBracket,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { SupportUserCandidate } from "../utils/supportUser";
import { TicketNeighbours } from "../utils/ticketNeighbours";

interface StaffTicketDetailActionsProps {
	neighbours: TicketNeighbours;
	/** False on a direct link: there is no queue page to step through (decision 4). */
	showArrows: boolean;
	supportUser: SupportUserCandidate | null;
	isLoggingIn: boolean;
	onNavigate: (ticketId: number) => void;
	onLoginAs: () => void;
}

/** The page header's right side (mockup `.l1`): Previous / Next through the queue page, Log in as. */
export const StaffTicketDetailActions: FunctionComponent<StaffTicketDetailActionsProps> = ({
	neighbours,
	showArrows,
	supportUser,
	isLoggingIn,
	onNavigate,
	onLoginAs,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex items-center gap-2">
			{showArrows && (
				<>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={neighbours.previous === null}
						image={<Icon className="mr-1.5" icon={faChevronLeft} />}
						size={ButtonSize.XS}
						title={t("support.staff.detail.previous")}
						type="button"
						onClick={() => neighbours.previous !== null && onNavigate(neighbours.previous)}
					/>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={neighbours.next === null}
						iconRight
						image={<Icon className="ml-1.5" icon={faChevronRight} />}
						size={ButtonSize.XS}
						title={t("support.staff.detail.next")}
						type="button"
						onClick={() => neighbours.next !== null && onNavigate(neighbours.next)}
					/>
				</>
			)}
			<Tooltip
				content={
					supportUser
						? t("support.staff.detail.loginAsHint", { email: supportUser.email })
						: t("support.staff.detail.noSupportUser")
				}
			>
				<span className="ml-2 flex">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={!supportUser || isLoggingIn}
						image={<Icon className="mr-1.5" icon={faRightToBracket} />}
						isLoading={isLoggingIn}
						size={ButtonSize.XS}
						title={t("support.staff.detail.loginAs")}
						type="button"
						onClick={onLoginAs}
					/>
				</span>
			</Tooltip>
		</div>
	);
};
